import { useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import { dayjs } from "@thalamos/common";
import {
  adminPatientDetailsReasons,
  type AdminPatientDetailsReason,
  type AdminPatientDetailsResponse,
} from "@thalamos/aspire-common";

import { api } from "../../api.js";
import {
  Button,
  LoadingSpinner,
  renderErrorToast,
} from "~/components/design-system/index.js";

const reasonLabels: Record<AdminPatientDetailsReason, string> = {
  "unknown-local-authority": "Unknown local authority",
  other: "Other",
};

const formatTimestamp = (value: string | null) =>
  value ? dayjs(value).format("D MMM YYYY HH:mm") : null;

function MergeTree({
  nodes,
}: {
  nodes: AdminPatientDetailsResponse["mergeTree"];
}) {
  const childrenOf = (patientId: string) =>
    nodes.filter(
      (node) =>
        node.parentPatientId === patientId && node.patientId !== patientId,
    );

  const root = nodes.find((node) => node.isRoot);

  if (!root) {
    return null;
  }

  const renderNode = (
    node: AdminPatientDetailsResponse["mergeTree"][number],
    depth: number,
  ) => (
    <Box key={node.patientId} sx={{ ml: depth * 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
          {depth > 0 ? "└─ " : ""}
          {node.patientId}
        </Typography>
        {node.isRoot ? (
          <Chip size="small" label="Root" color="primary" />
        ) : null}
        {node.isEntered ? (
          <Chip size="small" label="Entered" variant="outlined" />
        ) : null}
        {node.restricted ? (
          <Chip size="small" label="Restricted" color="error" />
        ) : null}
        {node.lastMerged ? (
          <Typography variant="caption" color="text.secondary">
            merged {formatTimestamp(node.lastMerged)}
          </Typography>
        ) : null}
      </Box>
      {childrenOf(node.patientId).map((child) => renderNode(child, depth + 1))}
    </Box>
  );

  return renderNode(root, 0);
}

export function PatientDetailsPage() {
  const [patientId, setPatientId] = useState("");
  const [reason, setReason] = useState<AdminPatientDetailsReason>(
    "unknown-local-authority",
  );
  const [description, setDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<AdminPatientDetailsResponse | null>(null);
  const [postcodeRevealed, setPostcodeRevealed] = useState(false);
  const [restrictedWarningOpen, setRestrictedWarningOpen] = useState(false);

  const descriptionRequired = reason === "other";
  const canSubmit =
    patientId.trim().length > 0 &&
    (!descriptionRequired || description.trim().length > 0);

  const lookup = async () => {
    setIsLoading(true);
    setData(null);
    setPostcodeRevealed(false);
    setRestrictedWarningOpen(false);

    try {
      const response = await api.admin.getPatientDetails({
        patientId: patientId.trim(),
        justification: {
          reason,
          ...(descriptionRequired ? { description: description.trim() } : {}),
        },
      });
      setData(response.data);
    } catch {
      renderErrorToast({
        message: "Could not look up that patient ID",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Patient details
      </Typography>

      <Alert severity="error" sx={{ mb: 3 }}>
        <AlertTitle>Consult the SOP before using this page</AlertTitle>
        This page shows identifiable data about a patient you have no care
        relationship with. Use it only for the purposes set out in the governing
        SOP, and only with a justification you could defend. Every lookup is
        audited against the patient and against the root patient of their merge
        tree.
      </Alert>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Patient ID"
            value={patientId}
            onChange={(event) => {
              setPatientId(event.target.value);
            }}
            placeholder="00000000-0000-0000-0000-000000000000"
            fullWidth
          />
          <TextField
            select
            label="Reason for access"
            value={reason}
            onChange={(event) => {
              setReason(event.target.value as AdminPatientDetailsReason);
            }}
            fullWidth
          >
            {adminPatientDetailsReasons.map((value) => (
              <MenuItem key={value} value={value}>
                {reasonLabels[value]}
              </MenuItem>
            ))}
          </TextField>
          {descriptionRequired ? (
            <TextField
              label="Reason description"
              value={description}
              onChange={(event) => {
                setDescription(event.target.value);
              }}
              multiline
              minRows={2}
              fullWidth
            />
          ) : null}
          <Box>
            <Button
              label="Look up patient"
              onClick={() => {
                void lookup();
              }}
              disabled={!canSubmit || isLoading}
              customStartIcon={<SearchIcon />}
            />
          </Box>
        </Box>
      </Paper>

      {isLoading ? <LoadingSpinner /> : null}

      {data ? (
        <Paper sx={{ p: 3 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Field</TableCell>
                  <TableCell>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>PDS linked</TableCell>
                  <TableCell>{data.pds.linked ? "Yes" : "No"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Last recorded PDS pull</TableCell>
                  <TableCell>
                    {formatTimestamp(data.pds.lastRecordedPullAt) ??
                      "No recorded pull"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>GP practice</TableCell>
                  <TableCell>
                    {data.gpPractice.odsCode === null
                      ? "Not recorded on PDS"
                      : `${data.gpPractice.odsCode}${
                          data.gpPractice.name
                            ? ` — ${data.gpPractice.name}`
                            : data.gpPractice.nameLookupFailed
                              ? " — name lookup failed"
                              : ""
                        }`}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Root patient</TableCell>
                  <TableCell>
                    {data.isRootPatient
                      ? "This is the root patient ID"
                      : data.rootPatientId}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Root patient postcode</TableCell>
                  <TableCell>
                    {data.postcodeWithheld ? (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="body2">Withheld</Typography>
                        <Button
                          label="Reveal"
                          variant="outlined"
                          onClick={() => {
                            setRestrictedWarningOpen(true);
                          }}
                          customStartIcon={<VisibilityOffIcon />}
                        />
                      </Box>
                    ) : (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {postcodeRevealed ? data.postcode : "•••• •••"}
                        </Typography>
                        <Button
                          label={postcodeRevealed ? "Hide" : "Reveal"}
                          variant="outlined"
                          onClick={() => {
                            setPostcodeRevealed(!postcodeRevealed);
                          }}
                          customStartIcon={
                            postcodeRevealed ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )
                          }
                        />
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Local authority</TableCell>
                  <TableCell>
                    {data.localAuthority
                      ? `${data.localAuthority.odsCode} — ${
                          data.localAuthority.name ?? "name unavailable"
                        }`
                      : data.localAuthorityLookupFailed
                        ? "Could not be resolved from the postcode"
                        : "No postcode held"}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {data.postcodeWithheld ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              This patient is restricted, so the postcode is withheld. The local
              authority above was resolved from it and is the answer to use.
            </Alert>
          ) : null}

          {restrictedWarningOpen ? (
            <Alert
              severity="warning"
              sx={{ mt: 2 }}
              onClose={() => {
                setRestrictedWarningOpen(false);
              }}
            >
              <AlertTitle>Postcode cannot be revealed</AlertTitle>
              This patient is restricted. Their postcode is never sent to this
              page, so there is nothing to reveal and no override. Use the local
              authority instead, and escalate via the SOP if you genuinely need
              the address.
            </Alert>
          ) : null}

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            Merge tree
          </Typography>
          <MergeTree nodes={data.mergeTree} />
        </Paper>
      ) : null}
    </Box>
  );
}
